import type {
  CollectionReference,
  DocumentData,
  QueryFilterConstraint,
} from 'firebase/firestore'
import {
  and,
  collection,
  doc,
  limit,
  or,
  orderBy,
  query,
  where,
  type Firestore,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { ZodError } from 'zod'
import { getDocsWithError, getDocWithError } from '../../firestore-mobx/fetch'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { UserSearch } from '@breakoutlearning/firebase-repository/stores/UserSearch'
import type { FirestoreUserSearch } from './schema'
import { schema } from './schema'
import { documentSnapshots } from '../../firestore-mobx/stream'

export * from './schema'

const converter: FirestoreDataConverter<FirestoreUserSearch> = {
  toFirestore: () => {
    throw new Error('Client cannot write FirestoreSearchUser')
  },
  fromFirestore: (snapshot: QueryDocumentSnapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })
    try {
      return schema.parse(data)
    } catch (e) {
      if (e instanceof ZodError) {
        console.error('Error PublicUser', e.errors)
      }
      throw e
    }
  },
}

const baseCollectionRef = (
  firestore: Firestore
): CollectionReference<FirestoreUserSearch, DocumentData> => {
  return collection(firestore, 'user_search').withConverter(converter)
}

export const fetchUserSearch = async (
  repository: FirebaseRepository,
  role: string | string[],
  term?: string
) => {
  const roleArray = role instanceof Array ? role : [role]
  const constraints: QueryFilterConstraint[] = [where('role', 'in', roleArray)]

  const originalTerm = term?.trim() || ''
  const safeTerm = originalTerm.toLowerCase()

  constraints.push(
    or(
      and(
        where('firstName', '>=', safeTerm),
        where('firstName', '<', safeTerm + '\uf8ff')
      ),
      and(
        where('emailAddress', '>=', safeTerm),
        where('emailAddress', '<', safeTerm + '\uf8ff')
      ),
      and(
        where('emailDomain', '>=', safeTerm),
        where('emailDomain', '<', safeTerm + '\uf8ff')
      ),
      and(
        where('lastName', '>=', safeTerm),
        where('lastName', '<', safeTerm + '\uf8ff')
      )
    )
  )

  const q = query(
    baseCollectionRef(repository.firestore),
    and(...constraints),
    term ? limit(100) : limit(1000),
    orderBy('lastName', 'asc')
  )

  const fieldResults = await getDocsWithError(q, 'SearchUserError')
  const results: UserSearch[] = fieldResults.docs.map((doc) => {
    const data = doc.data()
    return new UserSearch(repository, doc.id, data)
  })

  if (originalTerm.length >= 5) {
    const docRef = doc(baseCollectionRef(repository.firestore), originalTerm)
    const docSnap = await getDocWithError(docRef, 'SearchUserError')

    if (docSnap.exists()) {
      const data = docSnap.data()
      if (roleArray.includes(data.role)) {
        const alreadyExists = results.some((user) => user.id === originalTerm)
        if (!alreadyExists) {
          results.push(new UserSearch(repository, originalTerm, data))
        }
      }
    }
  }

  return results
}

export const getUserSearch = (
  repository: FirebaseRepository,
  userId: string
) => {
  const docRef = doc(baseCollectionRef(repository.firestore), userId)

  return documentSnapshots(docRef).map((snapshot) => {
    if (!snapshot.exists())
      throw new Error(`SearchUser with ID ${userId} does not exist`)
    return new UserSearch(repository, snapshot.id, snapshot.data())
  })
}
